import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, Search, Home as HomeIcon, BookOpen, Info, Mail, ShoppingBag, Wrench } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { TIER_LABELS } from "./CatalogMenu";
import { trackContact } from "@/lib/trackContact";

const TIER_ORDER = ["italian", "premium", "popular", "utility", "french", "japanese", "korean", "american", "other"];
const norm = (s) => (s || "").toString().toLowerCase().trim();

export default function MobileNav() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [openBrand, setOpenBrand] = useState(null); // brand name
  const [brands, setBrands] = useState([]);
  const [modelsByBrand, setModelsByBrand] = useState({});
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (open && brands.length === 0) {
      api.get("/vehicles/brands-summary")
        .then((r) => setBrands((r.data || []).filter((b) => b.products_count > 0)))
        .catch(() => setBrands([]));
    }
  }, [open, brands.length]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const close = () => {
    setOpen(false);
    setCatalogOpen(false);
    setOpenBrand(null);
    setQuery("");
  };

  const loadModels = async (brandName) => {
    if (modelsByBrand[brandName]) return;
    try {
      const { data } = await api.get("/products", { params: { brand: brandName } });
      const counts = new Map();
      for (const p of data || []) {
        const m = (p.model || "").trim();
        if (!m) continue;
        counts.set(m, (counts.get(m) || 0) + 1);
      }
      const list = Array.from(counts.entries())
        .map(([model, count]) => ({ model, count }))
        .sort((a, b) => a.model.localeCompare(b.model));
      setModelsByBrand((prev) => ({ ...prev, [brandName]: list }));
    } catch {
      setModelsByBrand((prev) => ({ ...prev, [brandName]: [] }));
    }
  };

  const toggleBrand = (brandName) => {
    if (openBrand === brandName) {
      setOpenBrand(null);
      return;
    }
    setOpenBrand(brandName);
    loadModels(brandName);
  };

  const filteredBrands = useMemo(() => {
    const q = norm(query);
    if (!q) return brands;
    return brands.filter((b) => norm(b.brand).includes(q));
  }, [brands, query]);

  const groups = useMemo(() => (
    TIER_ORDER
      .map((tier) => ({
        tier,
        label: TIER_LABELS[tier]?.[lang] || TIER_LABELS[tier]?.it || tier,
        brands: filteredBrands.filter((b) => b.tier === tier).sort((a, b) => a.brand.localeCompare(b.brand)),
      }))
      .filter((g) => g.brands.length > 0)
  ), [filteredBrands, lang]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="mobile-nav-open"
        className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-slate-200 hover:text-white hover:bg-xd-dark2 transition-colors"
        aria-label={lang === "en" ? "Open menu" : "Apri menu"}
      >
        <Menu className="w-5 h-5" />
      </button>

      {open && createPortal((
        <div className="md:hidden fixed inset-0 z-[100]" data-testid="mobile-nav-panel">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={close}
          />
          <div className="absolute inset-y-0 right-0 w-[88vw] max-w-sm bg-xd-dark border-l border-xd-darkBorder shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-xd-darkBorder">
              <div className="font-display text-white font-semibold">audrivio</div>
              <button
                type="button"
                onClick={close}
                data-testid="mobile-nav-close"
                className="w-9 h-9 rounded-lg text-slate-300 hover:text-white hover:bg-xd-dark2 flex items-center justify-center"
                aria-label={lang === "en" ? "Close menu" : "Chiudi menu"}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 min-h-0 overflow-y-auto py-3">
              <MobileLink to="/" icon={HomeIcon} label={t("nav_home")} onClick={close} />

              {/* Catalog accordion */}
              <button
                type="button"
                onClick={() => setCatalogOpen((v) => !v)}
                data-testid="mobile-nav-catalog-toggle"
                aria-expanded={catalogOpen}
                className="w-full flex items-center justify-between gap-3 px-5 py-3 text-sm text-slate-200 hover:bg-xd-dark2"
              >
                <span className="inline-flex items-center gap-3">
                  <ShoppingBag className="w-4 h-4 text-xd-cyan" />
                  {t("nav_catalog")}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${catalogOpen ? "rotate-180" : ""}`} />
              </button>

              {catalogOpen && (
                <div className="bg-xd-dark2/60 border-y border-xd-darkBorder">
                  <div className="px-5 py-3 flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="mobile-nav-brand-search"
                        placeholder={lang === "en" ? "Search brand…" : "Cerca marca…"}
                        className="w-full bg-xd-dark border border-xd-darkBorder text-white placeholder:text-slate-500 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-xd-cyan"
                      />
                    </div>
                    <Link
                      to="/catalogo"
                      onClick={close}
                      data-testid="mobile-nav-catalog-full"
                      className="text-[11px] font-medium text-xd-cyan whitespace-nowrap border border-xd-cyan/40 rounded-md px-2.5 py-2"
                    >
                      {lang === "en" ? "All" : "Tutti"}
                    </Link>
                  </div>

                  <div className="px-3 pb-3 max-h-[55vh] overflow-y-auto">
                    {groups.length === 0 ? (
                      <div className="text-xs text-slate-400 px-2 py-4 text-center">
                        {lang === "en" ? "No brand matches." : "Nessuna marca."}
                      </div>
                    ) : groups.map((g) => (
                      <div key={g.tier} className="mb-3">
                        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-orange px-2 mb-1">
                          {g.label}
                        </div>
                        <ul>
                          {g.brands.map((b) => {
                            const isOpen = openBrand === b.brand;
                            const models = modelsByBrand[b.brand];
                            return (
                              <li key={b.brand} className="border-b border-xd-darkBorder/40 last:border-b-0">
                                <button
                                  type="button"
                                  onClick={() => toggleBrand(b.brand)}
                                  data-testid={`mobile-nav-brand-${b.brand}`}
                                  aria-expanded={isOpen}
                                  className="w-full flex items-center justify-between px-2 py-2 text-sm text-slate-200 hover:text-white"
                                >
                                  <span className="font-medium">{b.brand}</span>
                                  <span className="flex items-center gap-2">
                                    <span className="font-mono-tech text-[10px] text-slate-500">{b.products_count}</span>
                                    <ChevronRight className={`w-3.5 h-3.5 transition-transform text-slate-500 ${isOpen ? "rotate-90 text-xd-cyan" : ""}`} />
                                  </span>
                                </button>

                                {isOpen && (
                                  <div className="pl-3 pr-2 pb-2 space-y-0.5">
                                    <Link
                                      to={`/marca/${encodeURIComponent(b.brand.toLowerCase().replace(/\s+/g, '-'))}`}
                                      onClick={close}
                                      className="block px-2 py-1.5 rounded text-xs text-xd-cyan hover:bg-xd-dark"
                                    >
                                      {lang === "en" ? "See all models" : "Vedi tutti i modelli"} →
                                    </Link>
                                    {!models ? (
                                      <div className="space-y-1 py-1">
                                        {[0, 1, 2].map((i) => (<div key={i} className="h-5 rounded bg-xd-dark animate-pulse" />))}
                                      </div>
                                    ) : models.length === 0 ? (
                                      <div className="text-[11px] text-slate-500 px-2 py-1">
                                        {lang === "en" ? "No models yet." : "Nessun modello."}
                                      </div>
                                    ) : (
                                      models.map((m) => (
                                        <Link
                                          key={m.model}
                                          to={`/catalogo?brand=${encodeURIComponent(b.brand)}&model=${encodeURIComponent(m.model)}`}
                                          onClick={close}
                                          data-testid={`mobile-nav-model-${b.brand}-${m.model}`}
                                          className="flex items-center justify-between px-2 py-1.5 rounded text-xs text-slate-300 hover:text-white hover:bg-xd-dark"
                                        >
                                          <span>{m.model}</span>
                                          <span className="font-mono-tech text-[10px] text-slate-500">{m.count}</span>
                                        </Link>
                                      ))
                                    )}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <MobileLink to="/accessori" icon={Wrench} label={t("nav_accessories")} onClick={close} />
              <MobileLink to="/blog" icon={BookOpen} label={t("nav_blog")} onClick={close} />
              <MobileLink to="/chi-siamo" icon={Info} label={t("nav_about")} onClick={close} />
              <MobileLink to="/contatti" icon={Mail} label={t("nav_contact")} onClick={close} />
            </nav>

            <div className="p-5 border-t border-xd-darkBorder">
              <a
                href="https://wa.me/393511294525?text=Ciao%20audrivio%2C%20vorrei%20maggiori%20informazioni"
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-nav-whatsapp"
                onClick={() => trackContact("whatsapp", "mobile_nav")}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold text-sm px-4 py-3 rounded-lg shadow-[0_0_18px_rgba(37,211,102,0.3)]"
              >
                {t("header_wa_cta")}
              </a>
            </div>
          </div>
        </div>
      ), document.body)}
    </>
  );
}

function MobileLink({ to, icon: Icon, label, onClick }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-5 py-3 text-sm ${isActive ? "text-xd-cyan bg-xd-dark2/50" : "text-slate-200 hover:bg-xd-dark2"}`
      }
    >
      <Icon className="w-4 h-4 text-xd-cyan" />
      {label}
    </NavLink>
  );
}
